import { useEffect, useState } from "react";
import InputPopover from "@/molecules/inputPopover";
import { popStyles, styles } from "./style";
import { CollectedDataType } from "@/common/type/type";
import {
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@/common/styles/style";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  AlertItemType,
  DataItemType,
  DataViewType,
} from "@/pages/dashboard/type";
import { getCursorStyle } from "@/helpers/getCursorStyle";

interface DataProps {
  collected?: CollectedDataType[];
  saved?: DataViewType;
}

export default function Data({ collected, saved }: DataProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLTableCellElement | null>(null);
  const [_, setSelectedName] = useState<string | null>(null);
  const [latestData, setLatestData] = useState<CollectedDataType[]>([]);
  const [savedData, setSavedData] = useState<DataItemType[]>([]);
  const [alertData, setAlertData] = useState<AlertItemType[]>([]);
  const [alertId, setAlertId] = useState(0);

  useEffect(() => {
    if (collected && collected.length > 0) {
      setLatestData(collected);
      setSavedData([]);
    } else if (saved?.data && saved?.data.length > 0) {
      setSavedData(saved.data);
      setLatestData([]);
    } else {
      setLatestData([]);
      setSavedData([]);
    }
  }, [collected, saved]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!saved?.data || !saved.machineId) {
        console.log("Invalid dataWidget or missing machineId");
        return;
      }

      try {
        for (const dataItem of saved.data) {
          for (const dataName of Object.keys(dataItem)) {
            const hasAlert = savedData?.some(
              (item) => item[dataName]?.hasAlert
            );

            if (hasAlert) {
              const queryString = `machineId=${saved.machineId}&dataName=${dataName}`;
              const response = await fetch(`/api/dashboard/widget/data/alert?${queryString}`, {
                  method: "GET",
                  credentials: "include",
              });

              const data = await response.json();
              if (response.ok) {
                setAlertData((prev) => {
                  const newAlertData: AlertItemType = {
                    [dataName]: data.result,
                  };

                  const isDuplicate = prev.some((item) => {
                    const existingData = item[dataName];
                    return (
                      existingData &&
                      existingData.some(
                        (alert) => alert.timestamp === data.result[0]?.timestamp
                      )
                    );
                  });

                  if (isDuplicate) {
                    return prev;
                  }

                  return [...prev, newAlertData];
                });
              } else {
                console.error("サーバ接続エラー:", response.statusText);
              }
            }
          }
        }
      } catch (error) {
        console.error("サーバ接続エラー:", error);
      }
    };
    fetchDashboardData();
  }, [savedData, saved]);

  const handlePopoverToggle = (
    event: React.MouseEvent<HTMLSpanElement>,
    name: string
  ) => {
    const target = event.target as HTMLTableCellElement;

    const foundItem = alertData.find((item) =>
      Object.keys(item).includes(name)
    );

    const alertId =
      foundItem && foundItem[name]?.length > 0
        ? foundItem[name][0].alertId
        : null;

    if (anchorEl === target) {
      setAnchorEl(null);
      setSelectedName(null);
    } else {
      setAnchorEl(target);
      setSelectedName(name);
      if (alertId !== null) {
        setAlertId(alertId);
      }
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setSelectedName(null);
  };

  const isPopoverOpen = Boolean(anchorEl);

  const handleExclamationClick = async (id: number) => {
    const requestBody = {
      alertIds: [id],
    };

    try {
      const response = await fetch("/api/dashboard/widget/data/alert", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      if (response.ok) {
        console.log(data.message);
      } else {
        console.error("サーバ接続エラー: ", response.statusText);
      }
    } catch (error) {
      console.error("サーバ接続エラー:", error);
    }
    setSelectedName(null);
    setAlertId(0);
  };

  return (
    <Box sx={styles.container}>
      {savedData.length === 0 && latestData.length === 0 ? (
        <Typography>データがありません</Typography>
      ) : (
        <Table sx={styles.table}>
          <TableBody>
            {savedData && latestData.length === 0
              ? savedData.map((data, index) => {
                  const key = Object.keys(data)[0];
                  const hasAlert = data[key]?.hasAlert ?? false;
                  // 保存したデータがある場合
                  return (
                    <TableRow
                      key={`saved-${index}`}
                      onClick={(event) => {
                        if (hasAlert) {
                          handlePopoverToggle(event, key);
                        }
                      }}
                      sx={getCursorStyle(hasAlert)}
                    >
                      {hasAlert ? (
                        <TableCell width="3%" sx={styles.tableCell}>
                          <Typography variant="body1" sx={styles.red}>
                            <ErrorOutlineIcon
                              fontSize="small"
                              sx={styles.errorIcon}
                            />
                          </Typography>
                        </TableCell>
                      ) : (
                        <TableCell sx={styles.tableCell} />
                      )}
                      <TableCell align="left" sx={styles.tableCell}>
                        <Typography
                          variant="body1"
                          sx={hasAlert ? styles.red : styles.black}
                        >
                          {key}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={styles.tableCell}>
                        <Typography
                          variant="body1"
                          sx={hasAlert ? styles.red : styles.black}
                        >
                          {data[key]
                            ? `${data[key].value}${data[key].unit ? ` ${data[key].unit}` : "   "}`
                            : "No Data"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              : latestData && savedData.length === 0
              ? latestData.map((data, index) => {
                  // 編集ポップで選択した場合
                  return (
                    <TableRow key={`selected-${index}`}>
                      <TableCell align="left" sx={styles.tableCell}>
                        <Typography variant="body1">{data.dataName}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={styles.tableCell}>
                        <Typography variant="body1">{data.value}</Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              : null}
          </TableBody>
        </Table>
      )}

      {/* ポップアップの表示 */}
      <InputPopover
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "center",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "left",
        }}
        disableRestoreFocus
        aria-hidden={isPopoverOpen ? "false" : "true"}
      >
        <Box sx={popStyles.container}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="h6">詳細データ</Typography>
            <Button
              variant="text"
              size="small"
              sx={popStyles.clearBottan}
              onClick={handlePopoverClose}
            >
              <Typography
                variant="button"
                onClick={() => handleExclamationClick(alertId)}
              >
                clear
              </Typography>
            </Button>
          </Stack>
          <Table>
            <TableBody>
              {alertData &&
                alertData.map((data, index) => {
                  const key = Object.keys(data)[0];
                  const dataItems = data[key];

                  return dataItems.map((item, idx) => (
                    <TableRow key={`${idx}-${index}`}>
                      <TableCell align="left">
                        <Typography variant="body1">
                          {item.timestamp}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1">{`${item.value} ${item.unit}`}</Typography>
                      </TableCell>
                    </TableRow>
                  ));
                })}
            </TableBody>
          </Table>
        </Box>
      </InputPopover>
    </Box>
  );
}
